import axios from 'axios';
import { ACCEPT } from './uploadLimits';

// '.pdf' -> 'application/pdf', '.docx' -> the DOCX type, from the dropzone's
// accept map so the two lists cannot drift.
const TYPE_BY_EXTENSION = Object.fromEntries(
    Object.entries(ACCEPT).flatMap(([type, extensions]) => extensions.map((ext) => [ext, type])),
);

/**
 * Content-type to send for a file. Must be byte-identical between the
 * `/case/upload-url` request (which signs the URL for this exact content
 * type) and the GCS PUT itself, or the signed URL will reject the upload.
 *
 * The extension decides first: browsers report an empty `file.type` for
 * some DOCX files, and the server refuses a `.docx` signed as PDF. The
 * browser type is the fallback for a name with no known extension, and
 * `null` means the file is neither PDF nor DOCX and must not be requested.
 *
 * @param {File} file
 * @returns {string|null}
 */
export const getUploadContentType = (file) => {
    const name = typeof file?.name === 'string' ? file.name.toLowerCase() : '';
    const dot = name.lastIndexOf('.');
    const extension = dot >= 0 ? name.slice(dot) : '';
    return TYPE_BY_EXTENSION[extension] || file?.type || null;
};

/**
 * PUT a file directly to a GCS signed URL, reporting progress as it goes.
 *
 * Deliberately uses bare axios rather than the `api` instance from
 * `src/api/api.js` — that instance's interceptors attach a Firebase auth
 * header and rewrite the base URL, neither of which GCS signed URLs accept
 * (a signed URL is pre-authorized via its query string; extra headers or a
 * different host will cause GCS to reject the request).
 *
 * @param {string} signedUrl - GCS V4 signed URL to PUT to.
 * @param {File} file - File to upload.
 * @param {string} contentType - Must match what was sent to /case/upload-url.
 * @param {{onProgress?: (p: {loaded: number, total: number, percent: number}) => void, signal?: AbortSignal, extraHeaders?: Record<string, string>}} [options]
 *   `extraHeaders` are the `required_headers` from /case/upload-url (the
 *   `x-goog-content-length-range` size cap); the V4 signature covers them,
 *   so the PUT must send them byte for byte or GCS rejects it.
 */
export async function uploadFileWithProgress(signedUrl, file, contentType, { onProgress, signal, extraHeaders } = {}) {
    await axios.put(signedUrl, file, {
        headers: { ...(extraHeaders || {}), 'Content-Type': contentType },
        signal,
        onUploadProgress: (event) => {
            if (!onProgress) return;
            const loaded = event.loaded ?? 0;
            const total = event.total ?? file.size ?? 0;
            const percent = total > 0 ? Math.round((loaded / total) * 100) : 0;
            onProgress({ loaded, total, percent });
        },
    });
}
